import { FieldLabel } from './../index';
import { FieldAttributes, useField, useFormikContext } from 'formik';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form, OverlayTrigger } from 'react-bootstrap';
import { FieldFeedback } from '.';
import FieldTooltip from './field-tooltip';
import { OptionType } from '../select-db';

type Props = {
  as?: React.ElementType<any>;
  label: string;
  min?: string;
  step?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  readonly onChange?: (any) => void;
} & FieldAttributes<any>;

const FieldNumericInput = ({
  as = 'div',
  name,
  label,
  min = '0',
  step = 'any',
  disabled = false,
  placeholder = '',
  required = false,
  onChange = null,
}: Props) => {
  //
  //-------------------- Body -----------------------------------------------
  //
  const [{ value }, { touched, error, initialValue }] = useField<number>(name);
  const { setFieldValue, setFieldTouched, submitCount } = useFormikContext<any>();
  const [actualValue, setActualValue] = useState<number>(value);

  useEffect(() => {
    if (value !== actualValue) setActualValue(value);
  }, [actualValue, value]);

  const setFieldValueAndTouched = useCallback(
    async (_value, touched: boolean) => {
      await setFieldValue(name, _value, true); //field,value,shouldValidate
      await setFieldTouched(name, touched); //field,touched,shouldValidate
    },
    [setFieldTouched, setFieldValue, name]
  );
  //
  //-------------------- Handlers -------------------------------------------
  //
  const handleChange = useCallback(
    (event) => {
      const _val = parseFloat(event.target.value);
      console.log('Numeric Change:', name, _val);
      setActualValue(_val);
      setFieldValueAndTouched(_val, true);
      if (onChange) onChange(_val);
    },
    [setFieldValueAndTouched, onChange, name]
  );

  const handleBlur = useCallback(async () => {
    await setFieldTouched(name, touched); //field,touched,shouldValidate
  }, [setFieldTouched, name, touched]);

  const changed = value !== initialValue;

  // useWhyDidYouUpdate(`[FieldNumberInput:${name}]`, {
  //   label,
  //   disabled,
  //   name,
  //   handleChange,
  //   handleBlur,
  //   setFieldValueAndTouched,
  //   value,
  //   initialValue,
  //   actualValue,
  // });
  //
  //-------------------- Render -------------------------------------------
  //
  return useMemo(
    () => (
      <Form.Group as={as} controlId={name} className="mb-2">
        <FieldLabel text={label} changed={changed} required={required} />
        <OverlayTrigger
          delay={{ show: 1000, hide: 200 }}
          overlay={<FieldTooltip text={name} value={value || ''} />}
          placement="top"
        >
          <Form.Control
            className="mb-1"
            style={
              required && !disabled ? { backgroundColor: 'lightYellow' } : {}
            }
            type="number"
            step={step}
            min={min}
            name={name}
            value={
              actualValue === null || actualValue === undefined
                ? ''
                : actualValue.toString()
            }
            placeholder={placeholder}
            isInvalid={(touched || submitCount > 0) && !!error}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </OverlayTrigger>
        <FieldFeedback type="invalid" name={name} />
      </Form.Group>
    ),
    [
      actualValue,
      as,
      changed,
      disabled,
      error,
      handleBlur,
      handleChange,
      label,
      min,
      name,
      placeholder,
      required,
      step,
      touched,
      submitCount,
      value,
    ]
  );
};

export default FieldNumericInput;
